import { IEcommerceAnswerSchema } from '../types'

// The backend serves EC products as a two-level tree: top-level answers (parents, standalones, N/A) with
// each parent's child SKUs nested in `children`, in display (valueOrder) order. The widget keeps ONE flat
// list for addressing — the cart, `schema.answers[index]`, id lookups and every retailer grid are written
// against a flat array — so the tree is flattened once at mount: each parent followed by its children.
// `children` is left on the parent as the family source of truth (see hooks/useChildrenProducts).
export function flattenFamilyTree(answers: IEcommerceAnswerSchema[] = []): IEcommerceAnswerSchema[] {
  return answers.flatMap(answer => [answer, ...(answer.children || [])])
}

interface IHiddenOptions {
  showHidden?: boolean
  suppressLogic?: boolean
}

// Researcher preview does not cut hidden nodes server-side (live does); it sends rules.show[id] = false and
// the widget marks products: `hidden` (display:none, like live) or `fakeHidden` (kept, greyed, not
// clickable — so the researcher sees the logic fired). The flat list is NOT filtered — the cart pairs
// response.answers[i] with schema.answers[i] by position — so hiding is a class on the card; a child is
// hidden by its own id (q{q}a{a}cs{c}) or along with a hidden parent; useChildrenProducts drops `hidden`
// children itself, while a `fakeHidden` child stays and the selector greys its value (useFamilyAxes).
export function markHiddenProducts(
  answers: IEcommerceAnswerSchema[] = [],
  showRules: Record<string, boolean> | undefined,
  { showHidden = false, suppressLogic = false }: IHiddenOptions = {}
): IEcommerceAnswerSchema[] {
  const isOff = (answer: IEcommerceAnswerSchema) => false === showRules?.[answer.id]
  const mark = (answer: IEcommerceAnswerSchema, parentOff = false) => {
    const off = parentOff || isOff(answer)
    // NOTE: deliberately not the other question types' formula (there "suppress logic" drops the greyed
    // mark); here "show hidden" always greys a ruled-out product and "suppress logic" only stops the drop
    answer.fakeHidden = showHidden && off
    answer.hidden = !showHidden && !suppressLogic && off
    if (answer.children) answer.children.forEach(child => mark(child, off))
  }
  // walk top-level only: in the flattened list children are also array members, and marking them as
  // roots would overwrite the parent's cascade
  answers.filter(answer => !answer.parent_product).forEach(answer => mark(answer))
  return answers
}
